/**
 * Unauthenticated password reset via dedicated `/reset-password/*` OTP routes.
 * Never call login OTP (`purpose: 'login'`) and never sign the user in from verify.
 */
import { isSuccessfulHttpStatus } from '../api/apiFetch';
import {
  formatHttpErrorDetails,
  formatThrownErrorDetails,
  friendlyHttpFailureSummary,
  friendlyNetworkErrorMessage,
} from '../api/httpErrorFormat';
import { fetchWithDevProxyFallback } from '../api/fetchWithDevProxyFallback';
import { isOtpRouteMissingOnServer, normalizeOtpCode, otpApiErrorMessage } from './emailOtpAuth';
import { isAssetOwnerLoginType } from './authEntryPolicy';
import type { LoginOption } from './loginConstants';
import { normalizePhoneForOtpWire } from './phoneOtpAuth';

/** Dedicated reset OTP purpose — login OTP rejects this with 400. */
export const PASSWORD_RESET_PURPOSE = 'password_reset' as const;

const DEFAULT_COMPLETE_PATH = 'reset-password/reset-password';

type PasswordResetApiResult = {
  ok: boolean;
  status: number;
  parsed: unknown;
  responseText: string;
  urlUsed: string;
  response: Response;
  pathUsed: string;
};

export type PasswordResetSendOutcome =
  | { ok: true }
  | { ok: false; friendly: string; details: string | null; userNotFound: boolean };

export type PasswordResetVerifyOutcome =
  | { ok: true; resetToken: string; expiresInSec: number | null }
  | { ok: false; friendly: string; details: string | null };

export type PasswordResetCompleteOutcome =
  | { ok: true }
  | { ok: false; friendly: string; details: string | null };

function pathCandidates(configured: string | undefined, defaults: readonly string[]): string[] {
  const paths = [configured?.trim(), ...defaults]
    .filter((value): value is string => Boolean(value?.trim()))
    .map((value) => value.replace(/^\/+/, ''))
    .filter(isPasswordResetApiPath);
  return Array.from(new Set(paths));
}

/** Reset lives on `/reset-password/*`. Never accept `login/` OTP tails. */
export function isPasswordResetApiPath(path: string): boolean {
  return path.replace(/^\/+/, '').startsWith('reset-password/');
}

function resetPasswordRolePrefix(loginType: LoginOption): string {
  if (isAssetOwnerLoginType(loginType)) {
    return 'reset-password/assetsowners';
  }
  if (loginType === 'business client') {
    return 'reset-password/BusinessClient';
  }
  if (loginType === 'business worker') {
    return 'reset-password/BusinessWorker';
  }
  return 'reset-password/clients';
}

function firstPath(configured: string | undefined, fallback: string): string {
  return pathCandidates(configured, [fallback])[0] ?? fallback;
}

export function passwordResetEmailSendPath(loginType: LoginOption): string {
  return firstPath(
    process.env.REACT_APP_PASSWORD_RESET_EMAIL_OTP_SEND_PATH,
    `${resetPasswordRolePrefix(loginType)}/otp/send`,
  );
}

export function passwordResetEmailVerifyPath(loginType: LoginOption): string {
  return firstPath(
    process.env.REACT_APP_PASSWORD_RESET_EMAIL_OTP_VERIFY_PATH,
    `${resetPasswordRolePrefix(loginType)}/otp/verify`,
  );
}

export function passwordResetPhoneSendPath(loginType: LoginOption): string {
  return firstPath(
    process.env.REACT_APP_PASSWORD_RESET_PHONE_OTP_SEND_PATH,
    `${resetPasswordRolePrefix(loginType)}/phone/otp/send`,
  );
}

export function passwordResetPhoneVerifyPath(loginType: LoginOption): string {
  return firstPath(
    process.env.REACT_APP_PASSWORD_RESET_PHONE_OTP_VERIFY_PATH,
    `${resetPasswordRolePrefix(loginType)}/phone/otp/verify`,
  );
}

export function passwordResetCompletePath(): string {
  return firstPath(process.env.REACT_APP_PASSWORD_RESET_COMPLETE_PATH, DEFAULT_COMPLETE_PATH);
}

/** Read `resetToken` only — never accessToken / cookies / session JWT. */
export function extractResetTokenFromUnknown(parsed: unknown): string | null {
  if (!parsed || typeof parsed !== 'object') {
    return null;
  }
  const record = parsed as Record<string, unknown>;
  const nested = record.data && typeof record.data === 'object' ? (record.data as Record<string, unknown>) : null;
  const candidates = [record.resetToken, record.reset_token, nested?.resetToken, nested?.reset_token];
  for (const value of candidates) {
    if (typeof value === 'string' && value.trim()) {
      return value.trim();
    }
  }
  return null;
}

export function extractExpiresInSecFromUnknown(parsed: unknown): number | null {
  if (!parsed || typeof parsed !== 'object') {
    return null;
  }
  const record = parsed as Record<string, unknown>;
  const nested = record.data && typeof record.data === 'object' ? (record.data as Record<string, unknown>) : null;
  const raw = record.expiresInSec ?? record.expires_in_sec ?? nested?.expiresInSec ?? nested?.expires_in_sec;
  if (typeof raw === 'number' && Number.isFinite(raw)) {
    return raw;
  }
  if (typeof raw === 'string' && raw.trim() && Number.isFinite(Number(raw))) {
    return Number(raw);
  }
  return null;
}

export function isPasswordResetUserMissingError(status: number, responseText: string): boolean {
  if (isOtpRouteMissingOnServer(status, responseText)) {
    return false;
  }
  if (status === 404) {
    return true;
  }
  const message = otpApiErrorMessage(responseText).toLowerCase();
  return /user does not exist|user not found|account not found|no user|not registered/.test(message);
}

function recordFailure(
  result: PasswordResetApiResult,
  body: Record<string, unknown>,
): { friendly: string; details: string | null } {
  return {
    friendly: friendlyHttpFailureSummary(result.status, '', result.responseText),
    details: formatHttpErrorDetails({
      requestUrl: result.urlUsed,
      requestPayload: body,
      response: result.response,
      responseText: result.responseText,
    }),
  };
}

async function postJson(apiPathTail: string, body: Record<string, unknown>): Promise<PasswordResetApiResult> {
  const path = apiPathTail.replace(/^\/+/, '');
  const { response, urlUsed } = await fetchWithDevProxyFallback(
    path,
    {
      method: 'POST',
      headers: {
        Accept: 'application/json, text/plain, */*',
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(body),
    },
    'api',
  );
  const responseText = await response.text();
  let parsed: unknown = null;
  if (responseText.trim()) {
    try {
      parsed = JSON.parse(responseText) as unknown;
    } catch {
      parsed = null;
    }
  }
  return {
    ok: isSuccessfulHttpStatus(response.status),
    status: response.status,
    parsed,
    responseText,
    urlUsed,
    response,
    pathUsed: path,
  };
}

async function sendPasswordResetOtp(
  path: string,
  body: Record<string, unknown>,
  fallbackFriendly: string,
): Promise<PasswordResetSendOutcome> {
  try {
    const result = await postJson(path, body);
    if (result.ok) {
      return { ok: true };
    }
    const failure = recordFailure(result, body);
    return {
      ok: false,
      friendly: failure.friendly || fallbackFriendly,
      details: failure.details,
      userNotFound: isPasswordResetUserMissingError(result.status, result.responseText),
    };
  } catch (error) {
    return {
      ok: false,
      friendly: friendlyNetworkErrorMessage(error),
      details: formatThrownErrorDetails(error),
      userNotFound: false,
    };
  }
}

async function verifyPasswordResetOtp(
  path: string,
  body: Record<string, unknown>,
  fallbackFriendly: string,
): Promise<PasswordResetVerifyOutcome> {
  try {
    const result = await postJson(path, body);
    if (result.ok) {
      const resetToken = extractResetTokenFromUnknown(result.parsed);
      if (resetToken) {
        return {
          ok: true,
          resetToken,
          expiresInSec: extractExpiresInSecFromUnknown(result.parsed),
        };
      }
      return {
        ok: false,
        friendly: 'The reset code was accepted but no reset token was returned.',
        details: formatHttpErrorDetails({
          requestUrl: result.urlUsed,
          requestPayload: body,
          response: result.response,
          responseText: result.responseText,
        }),
      };
    }
    const failure = recordFailure(result, body);
    return {
      ok: false,
      friendly: failure.friendly || fallbackFriendly,
      details: failure.details,
    };
  } catch (error) {
    return {
      ok: false,
      friendly: friendlyNetworkErrorMessage(error),
      details: formatThrownErrorDetails(error),
    };
  }
}

export async function sendPasswordResetEmailOtp(
  email: string,
  loginType: LoginOption,
): Promise<PasswordResetSendOutcome> {
  const normalizedEmail = email.trim();
  return sendPasswordResetOtp(
    passwordResetEmailSendPath(loginType),
    { email: normalizedEmail, purpose: PASSWORD_RESET_PURPOSE },
    'Could not send a reset code.',
  );
}

export async function sendPasswordResetPhoneOtp(
  phone: string,
  loginType: LoginOption,
): Promise<PasswordResetSendOutcome> {
  const phoneNumber = normalizePhoneForOtpWire(phone);
  return sendPasswordResetOtp(
    passwordResetPhoneSendPath(loginType),
    { phone_number: phoneNumber, purpose: PASSWORD_RESET_PURPOSE },
    'Could not send a reset code.',
  );
}

export async function verifyPasswordResetEmailOtp(
  email: string,
  code: string,
  loginType: LoginOption,
): Promise<PasswordResetVerifyOutcome> {
  const normalizedEmail = email.trim();
  const normalizedCode = normalizeOtpCode(code);
  return verifyPasswordResetOtp(
    passwordResetEmailVerifyPath(loginType),
    { email: normalizedEmail, code: normalizedCode, purpose: PASSWORD_RESET_PURPOSE },
    'Could not verify the reset code.',
  );
}

export async function verifyPasswordResetPhoneOtp(
  phone: string,
  code: string,
  loginType: LoginOption,
): Promise<PasswordResetVerifyOutcome> {
  const phoneNumber = normalizePhoneForOtpWire(phone);
  const normalizedCode = normalizeOtpCode(code);
  return verifyPasswordResetOtp(
    passwordResetPhoneVerifyPath(loginType),
    { phone_number: phoneNumber, code: normalizedCode, purpose: PASSWORD_RESET_PURPOSE },
    'Could not verify the reset code.',
  );
}

export async function completePasswordReset(
  resetToken: string,
  newPassword: string,
): Promise<PasswordResetCompleteOutcome> {
  const token = resetToken.trim();
  const password = newPassword.trim();
  const body = { token, newPassword: password };
  try {
    const result = await postJson(passwordResetCompletePath(), body);
    if (result.ok) {
      return { ok: true };
    }
    const failure = recordFailure(result, { token: '***', newPassword: '***' });
    return {
      ok: false,
      friendly: failure.friendly || 'Could not update the password. Try again.',
      details: failure.details,
    };
  } catch (error) {
    return {
      ok: false,
      friendly: friendlyNetworkErrorMessage(error),
      details: formatThrownErrorDetails(error),
    };
  }
}

export { normalizeOtpCode };
